import useQuery from "./useQuery";
import useQueryStartSession, { IStartSession, IStartSessionResponse } from "./useQueryStartSession";
import { getRandomRequestId } from "taskpane/utils";
import useQueryEndSession from "./useQueryEndSession";
import { ConferenceStatusCode, QueryType } from "taskpane/models/enums";
import { IConferenceQueryResult, IConferenceStatusResponse, IQueryResponse } from "taskpane/models/interfaces";
import { OnReady } from "taskpane/models/types";
import {
  getQueryErrorDeauthorization,
  getQueryErrorDescription,
  getQueryErrorPermissionDenied,
} from "taskpane/resources";

interface IGetConferencePostParams {
  reqId: string;
  aimsid: string;
  params: {
    id: string;
  };
}

export interface IGetConference extends IStartSession {
  conferenceId: string;
}

export interface IGetConferenceResponse extends IConferenceStatusResponse {
  results: {
    roles: {
      default: {
        permissions: number;
      };
    };
  };
}

const useQueryGetConference = (
  params: IGetConference,
  onReady: OnReady<IConferenceQueryResult<IStartSessionResponse | IGetConferenceResponse>>
) => {
  const { token, email, apiUrl, conferenceId } = params;
  const startSessionParams: IStartSession = {
    token,
    email,
    apiUrl,
  };

  useQueryStartSession(startSessionParams, (responseStartSession: IConferenceQueryResult<IStartSessionResponse>) => {
    if (responseStartSession.errorMessage) {
      return onReady(responseStartSession);
    }

    const aimsid: string = responseStartSession.response.response.data.aimsid;
    const url: string = `${apiUrl}/rapi/conference/get`;
    const postParams: IGetConferencePostParams = {
      reqId: getRandomRequestId(),
      aimsid: aimsid,
      params: {
        id: conferenceId,
      },
    };

    useQuery(
      url,
      "POST",
      (responseGetConference: IQueryResponse<IGetConferenceResponse>) => {
        const response: IConferenceQueryResult<IGetConferenceResponse> = {
          queryType: QueryType.getConference,
          ...responseGetConference,
        };

        if (responseGetConference.errorMessage) {
          return onReady(response);
        }

        const { code: statusCode, reason: statusText } = responseGetConference.response.status;

        if (statusCode === ConferenceStatusCode.notAuthorized) {
          return onReady({
            ...response,
            errorMessage: getQueryErrorDeauthorization(),
          });
        } else if (statusCode === ConferenceStatusCode.permissionDenied) {
          return onReady({
            ...response,
            errorMessage: getQueryErrorPermissionDenied(),
          });
        } else if (statusCode !== ConferenceStatusCode.succeeded) {
          return onReady({
            ...response,
            errorMessage: getQueryErrorDescription(statusCode, statusText),
          });
        }

        useQueryEndSession({
          aimsid: aimsid,
          apiUrl: apiUrl,
        });

        onReady(response);
      },
      { data: postParams }
    );
  });
};

export default useQueryGetConference;
